import { Component, Inject, signal, computed, effect } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { Tenant } from '../../models/tenant.model';
import { UpdateTenantRequest } from '../../models/update-tenant.request';
import { TenantService } from '../../services/tenant.service';
import { LoadingSpinnerComponent } from '../../../../shared/components/loading-spinner/loading-spinner.component';

type TenantFormValue = {
  tenantName: string;
  tenantAlias: string;
  marketplaceSubscriptionId: string;
  collectUserLocation: boolean;
  hasDashboardsAccess: boolean;
  hasPresentationsAccess: boolean;
  hasCognitionAccess: boolean;
};

@Component({
  selector: 'app-edit-tenant-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSlideToggleModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    LoadingSpinnerComponent
  ],
  templateUrl: './edit-tenant-dialog.component.html',
  styleUrl: './edit-tenant-dialog.component.css'
})
export class EditTenantDialogComponent {
  readonly isSubmitting = signal(false);
  readonly submissionErrorMessage = signal('');
  private readonly initialFormValue: TenantFormValue;
  private readonly formValue = signal<TenantFormValue>({} as TenantFormValue);

  editTenantForm: FormGroup;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<EditTenantDialogComponent, UpdateTenantRequest | undefined>,
    private tenantService: TenantService,
    @Inject(MAT_DIALOG_DATA) public tenant: Tenant
  ) {
    this.editTenantForm = this.fb.group({
      tenantName: [tenant.tenantName ?? '', [Validators.required]],
      tenantAlias: [tenant.tenantAlias ?? '', [Validators.required]],
      marketplaceSubscriptionId: [tenant.marketplaceSubscriptionId ?? ''],
      collectUserLocation: [tenant.collectUserLocation ?? false],
      hasDashboardsAccess: [tenant.hasDashboardsAccess ?? false],
      hasPresentationsAccess: [tenant.hasPresentationsAccess ?? false],
      hasCognitionAccess: [tenant.hasCognitionAccess ?? false]
    });

    this.initialFormValue = this.normalizeFormValue(this.editTenantForm.getRawValue() as TenantFormValue);
    this.formValue.set(this.initialFormValue);

    // Subscribe to form changes to update signal
    this.editTenantForm.valueChanges.subscribe(() => {
      this.formValue.set(this.editTenantForm.getRawValue() as TenantFormValue);
    });
  }

  hasFormChanges(): boolean {
    const current = this.normalizeFormValue(this.editTenantForm.getRawValue() as TenantFormValue);
    return Object.keys(current).some(key => {
      const typedKey = key as keyof TenantFormValue;
      return current[typedKey] !== this.initialFormValue[typedKey];
    });
  }

  private normalizeFormValue(value: TenantFormValue | Partial<TenantFormValue>): TenantFormValue {
    return {
      tenantName: (value.tenantName ?? '').trim(),
      tenantAlias: (value.tenantAlias ?? '').trim(),
      marketplaceSubscriptionId: (value.marketplaceSubscriptionId ?? '').trim(),
      collectUserLocation: value.collectUserLocation ?? false,
      hasDashboardsAccess: value.hasDashboardsAccess ?? false,
      hasPresentationsAccess: value.hasPresentationsAccess ?? false,
      hasCognitionAccess: value.hasCognitionAccess ?? false
    };
  }

  onSubmit(): void {
    if (this.editTenantForm.invalid || this.isSubmitting() || !this.hasFormChanges()) {
      if (this.editTenantForm.invalid) {
        this.editTenantForm.markAllAsTouched();
      }
      return;
    }

    this.isSubmitting.set(true);
    this.submissionErrorMessage.set('');

    const current = this.normalizeFormValue(this.editTenantForm.getRawValue() as TenantFormValue);
    const changes: Partial<UpdateTenantRequest> = { tenantId: this.tenant.tenantId };

    // Only include changed fields
    if (current.tenantName !== this.initialFormValue.tenantName) {
      changes.tenantName = current.tenantName;
    }
    if (current.tenantAlias !== this.initialFormValue.tenantAlias) {
      changes.tenantAlias = current.tenantAlias;
    }
    if (current.marketplaceSubscriptionId !== this.initialFormValue.marketplaceSubscriptionId) {
      // Send empty string to clear, or the actual value
      changes.marketplaceSubscriptionId = current.marketplaceSubscriptionId || '';
    }
    if (current.collectUserLocation !== this.initialFormValue.collectUserLocation) {
      changes.collectUserLocation = current.collectUserLocation;
    }
    if (current.hasDashboardsAccess !== this.initialFormValue.hasDashboardsAccess) {
      changes.hasDashboardsAccess = current.hasDashboardsAccess;
    }
    if (current.hasPresentationsAccess !== this.initialFormValue.hasPresentationsAccess) {
      changes.hasPresentationsAccess = current.hasPresentationsAccess;
    }
    if (current.hasCognitionAccess !== this.initialFormValue.hasCognitionAccess) {
      changes.hasCognitionAccess = current.hasCognitionAccess;
    }

    this.tenantService.updateTenant(changes as UpdateTenantRequest).subscribe({
      next: (updatedTenant) => {
        this.isSubmitting.set(false);
        this.dialogRef.close(updatedTenant);
      },
      error: (error: string) => {
        this.isSubmitting.set(false);
        this.submissionErrorMessage.set(error ?? 'Failed to update tenant.');
      }
    });
  }

  onCancel(): void {
    if (this.isSubmitting()) return;
    this.dialogRef.close();
  }

  showCognitionWarning = computed(() => {
    const originalValue = this.tenant.hasCognitionAccess;
    const currentValue = this.formValue().hasCognitionAccess;
    return !originalValue && currentValue;
  });
}